import { useEffect, useState } from 'react';

// Installing the website as a desktop app (Chrome and Edge on Mac, Windows
// and Linux; Safari on Mac through File > Add to Dock). Browsers offer the
// install once, early, so the offer is caught as soon as the page loads.

let deferredPrompt = null;
const listeners = new Set();
const notify = () => listeners.forEach((listener) => listener());

if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (event) => {
    // Keep the browser's offer for our own Install button
    event.preventDefault();
    deferredPrompt = event;
    notify();
  });
  window.addEventListener('appinstalled', () => {
    deferredPrompt = null;
    notify();
  });
}

// Running in the downloadable desktop app (desktop/src/preload.js)
export const isDesktopApp = () => typeof window !== 'undefined' && Boolean(window.tuneswellDesktop);

// Already running as the installed app
export const isInstalledApp = () =>
  typeof window !== 'undefined' && (
    isDesktopApp() ||
    window.matchMedia?.('(display-mode: standalone)').matches ||
    window.matchMedia?.('(display-mode: window-controls-overlay)').matches ||
    window.navigator.standalone === true
  );

const userAgent = () => (typeof navigator === 'undefined' ? '' : navigator.userAgent);
export const isPhoneOrTablet = () => /Android|iPhone|iPad|iPod|Mobile/i.test(userAgent());
// Safari on a Mac (not Chrome, Edge or Firefox, which also say "Safari")
const isMacSafari = () => /Macintosh/.test(userAgent()) && /Safari/.test(userAgent())
  && !/Chrome|Chromium|Edg|Firefox|FxiOS|CriOS/.test(userAgent());

// What the person's browser can do:
//   'prompt'  the browser can install it on request
//   'safari'  Safari on a Mac: File > Add to Dock
//   null      installed already, a phone (the phone app is better), or a
//             browser that can't install websites
export const useInstallApp = () => {
  const [, rerender] = useState(0);
  useEffect(() => {
    const listener = () => rerender((count) => count + 1);
    listeners.add(listener);
    return () => listeners.delete(listener);
  }, []);

  let how = null;
  if (!isInstalledApp() && !isPhoneOrTablet()) {
    if (deferredPrompt) how = 'prompt';
    else if (isMacSafari()) how = 'safari';
  }

  const install = async () => {
    if (!deferredPrompt) return false;
    const prompt = deferredPrompt;
    deferredPrompt = null;
    prompt.prompt();
    const { outcome } = await prompt.userChoice;
    notify();
    return outcome === 'accepted';
  };

  return { how, install };
};
